import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { AppIcons } from '../../../core/icons/fa-icons';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../shared/services/monitoring.service';
import { Document } from '../../leads/document/document';
import { BackButton } from '../../shared/buttons/back-button';
import { CraditService } from '../../credit/shared/services/cradit.service';
import { Setup } from '../../setup/shared/service/setup';
import { LeadService } from '../../leads/shared/service/leads.service';
import {
  CovenantResponseDto,
  SaveCovenantResponsesPayload,
} from '../../credit/shared/models/proposed-disbursement.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { MODULE_SEQ, PKL_GRP, QSTNR_SEQ } from '../../../core/shared/models/picklist.model';
import {
  MonitoringItemDto,
  monitoringStatusOf,
  SaveMonitoringPayload,
} from '../shared/models/monitoring.model';

type MonitoringTab = 'outputs' | 'findings' | 'covenants' | 'report';

/**
 * Fields a covenant response may carry its own wording in. The by-client
 * endpoint is not described in the service's OpenAPI document, so the known
 * spellings are tried in order before falling back to the questionnaire.
 */
const COVENANT_TEXT_KEYS = ['qstStr', 'qstnStr', 'qsntnStr', 'question', 'qstnDscr', 'dscrptn'];

/**
 * One row of the covenant tab. `ansVal` and `rmrks` are editable; their
 * originals are kept so a save sends just the rows that actually changed.
 * `qrtr` is read-only and sent back as loaded.
 */
interface CovenantRow {
  crdtCnvntRspSeq: number;
  crdtPrpslSeq: number;
  qsntnSeq: number;
  question: string;
  rmrks: string;
  originalRmrks: string;
  qrtr: string;
  delFlg: number;

  /** Ref group 46 code; null when unanswered. */
  ansVal: number | null;
  originalAnsVal: number | null;
}

/**
 * Monitoring workspace for one client.
 *
 * Three tabs:
 *  - Measurable Outputs — read-only view of `get-pending-mntrng` (previous
 *    pending) and `get-prev-mntrng` (previous year)
 *  - Agreed Action Plan — new items captured here and posted to `save-mntrng`
 *  - Covenant Compliance — every known monitoring item with its status
 *    editable, saved back through the same `save-mntrng` endpoint
 *  - Monitoring Report   — the client's monitoring report documents, through
 *    the shared document component
 *
 * All three read the same rows; only the status tab may change them, and only
 * their status.
 */
@Component({
  selector: 'app-monitoring-detail',
  imports: [CommonModule, FormsModule, Table, Document, BackButton, TranslatePipe],
  templateUrl: './monitoring-detail.html',
})
export class MonitoringDetail implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private monitoringService: MonitoringService,
    private creditService: CraditService,
    private setup: Setup,
    private pickListService: PickListService,
    private toast: ToastService,
    private leadService: LeadService,
    private translate: TranslateService,
  ) {}

  /** The Monitoring Report tab's read-only document listing — reloaded after an upload. */
  @ViewChild(Document) documentComponent?: Document;

  clntSeq = 0;
  clntNm = '';

  // "Measurable Outputs" tab is hidden on the frontend, so land on Findings.
  activeTab: MonitoringTab = 'findings';

  // ================= REPORT =================
  /** Document category the monitoring reports are filed under. */
  readonly reportDocCategory = MODULE_SEQ.MONITORING_REPORT;

  /**
   * Kept alive but hidden once opened: the shared document component holds a
   * part-filled upload form, so destroying it on a tab switch would lose it.
   */
  reportVisited = false;

  // ================= UPLOAD MONITORING REPORT POPUP =================
  // Same visual style as the shared Document component's own upload modal
  // (see leads/document/document.html), but with "Document Name" swapped for
  // a "Visit Date" field the user fills per upload.
  showUploadReportPopup = false;
  uploadReportFile: File | null = null;
  uploadReportDocTyp: number | null = null;
  uploadReportVisitDate = '';
  uploadReportDocTypeOptions: { label: string; value: number }[] = [];
  savingUploadReport = false;

  openUploadReportPopup(): void {
    this.uploadReportFile = null;
    this.uploadReportDocTyp = null;
    this.uploadReportVisitDate = '';
    this.showUploadReportPopup = true;

    this.loadUploadReportDocTypes();
  }

  closeUploadReportPopup(): void {
    this.showUploadReportPopup = false;
  }

  /** GET /setup/get-all-docs/{entyTyp} — document types the "Document Type" dropdown lists. */
  private loadUploadReportDocTypes(): void {
    this.setup.getAllDocumentByCategry(this.reportDocCategory).subscribe({
      next: (response: any[]) => {
        const list = Array.isArray(response) ? response : [];

        this.uploadReportDocTypeOptions = list
          .filter((item) => !item.delFlg)
          .map((item) => ({ label: item.docNm, value: item.docSeq }));
      },

      error: () => {
        this.uploadReportDocTypeOptions = [];
        this.toast.error('MONITORING.DOC_TYPES_LOAD_FAILED');
      },
    });
  }

  /** Report files are accepted as PDF or Word only. */
  private readonly ALLOWED_REPORT_EXTENSIONS = ['pdf', 'doc', 'docx'];

  onUploadReportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.uploadReportFile = null;
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!this.ALLOWED_REPORT_EXTENSIONS.includes(extension)) {
      this.uploadReportFile = null;
      input.value = '';
      this.toast.error('MONITORING.ONLY_PDF_WORD');
      return;
    }

    this.uploadReportFile = file;
  }

  /**
   * POST /leads/upload-lead-docs — same call and same `FormData` shape the
   * shared Document component already uses for every other upload
   * (leads/document/document.ts `handleDynamicSubmit`), just with `docTyp`,
   * `entySeq`/`entyTyp` and `docDt` filled in for this screen.
   */
  saveUploadReport(): void {
    if (!this.uploadReportDocTyp) {
      this.toast.error('MONITORING.SELECT_DOC_TYPE');
      return;
    }

    if (!this.uploadReportVisitDate) {
      this.toast.error('MONITORING.SELECT_VISIT_DATE');
      return;
    }

    if (!this.uploadReportFile) {
      this.toast.error('MONITORING.SELECT_FILE');
      return;
    }

    if (!this.clntSeq) {
      this.toast.error('MONITORING.CLIENT_SEQ_UNAVAILABLE');
      return;
    }

    this.savingUploadReport = true;

    const formData = new FormData();

    formData.append('docImg', this.uploadReportFile);
    formData.append('entySeq', this.clntSeq.toString());
    formData.append('entyTyp', this.reportDocCategory.toString());
    formData.append('docTyp', String(this.uploadReportDocTyp));
    formData.append('docDt', this.uploadReportVisitDate);
    formData.append('delFlg', 'false');

    this.leadService.saveLeadDocs(formData).subscribe({
      next: () => {
        this.savingUploadReport = false;
        this.toast.success('MONITORING.REPORT_UPLOADED');
        this.showUploadReportPopup = false;

        this.documentComponent?.loadDocuments();
      },

      error: (error: any) => {
        this.savingUploadReport = false;

        console.error('Upload monitoring report error:', error);

        this.toast.error(error?.error?.message ?? 'MONITORING.REPORT_UPLOAD_FAILED');
      },
    });
  }

  // ================= MEASURABLE OUTPUTS =================
  readonly itemColumns: TableColumn[] = [
    { key: 'dscrptn', label: 'MONITORING.MEASURABLE_OUTPUT' },
    { key: 'rmrks', label: 'COMMON.REMARKS' },
    { key: 'stsLabel', label: 'COMMON.STATUS' },
    { key: 'evntDt', label: 'MONITORING.VISIT_DATE', type: 'date' },
  ];

  /** Opens a monitoring item in the findings tab for editing. */
  readonly outputActions: TableAction[] = [
    {
      icon: AppIcons.edit,
      tooltip: 'MONITORING.EDIT_IN_ACTION_PLAN',
      class: 'text-indigo-600',
      onClick: (row: MonitoringItemDto) => this.respondToPendingItem(row),
    },
  ];

  pendingRows: MonitoringItemDto[] = [];
  previousRows: MonitoringItemDto[] = [];


  outputsLoading = false;

  /** Set only when the screen cannot be loaded at all (no client sequence). */
  outputsError = '';

  // Each grid reports its own outcome. A call that fails must never render as
  // "nothing pending" — an empty grid and a failed request look identical to
  // the user otherwise, and the failure goes unnoticed.
  pendingError = '';
  previousError = '';

  // ================= FINDINGS =================
  /** Date of the monitoring event the new findings belong to. */
  evntDt = new Date().toISOString().slice(0, 10);

  findings: MonitoringItemDto[] = [];
  savingFindings = false;

  /**
   * Findings already recorded for this client in the year of `evntDt`, shown
   * read-only beneath the form. Reloaded after every save and whenever the
   * event date moves into a different year.
   */
  savedItems: MonitoringItemDto[] = [];

  savedItemsLoading = false;
  savedItemsError = '';

  /** Year the listing below the form is currently showing. */
  private loadedFindingsYear = 0;

  /** `stsKey` is a common code from ref group {@link PKL_GRP.MONITORING_STATUS}. */
  statusMap: Record<number, string> = {};

  readonly savedItemColumns: TableColumn[] = [
    { key: 'dscrptn', label: 'MONITORING.ACTION_PLAN' },
    { key: 'rmrks', label: 'COMMON.REMARKS' },
    { key: 'stsLabel', label: 'COMMON.STATUS' },
    { key: 'evntDt', label: 'MONITORING.VISIT_DATE', type: 'date' },
  ];

  /** Sourced from ref group 38; empty until that call returns. */
  statusOptions: { value: number; label: string }[] = [];

  // ================= COVENANTS =================
  /** Covenant responses for this client, answer editable. */
  covenantRows: CovenantRow[] = [];

  /**
   * Answer options from ref group 46, so adding or rewording a compliance
   * state is a setup change rather than a code change. The chosen code is
   * saved as the response's `stsKey`.
   */
  covenantOptions: { value: number; label: string }[] = [];

  savingCovenants = false;
  covenantsLoading = false;
  covenantsError = '';

  /** Current quarter from the by-client response (`currentQrtr` / `qrtr`), e.g. "Oct-26". */
  covenantQuarter = '';

  /** Keeps the missing-wording warning to one line per screen. */
  private covenantTextWarned = false;

  /** True while a coordinated reload of the monitoring sources is in flight. */
  statusLoading = false;

  ngOnInit(): void {
    this.clntSeq = Number(this.route.snapshot.paramMap.get('clntSeq') ?? 0);
    this.clntNm = this.route.snapshot.queryParamMap.get('clntNm') ?? '';

    if (this.clntSeq <= 0) {
      this.outputsError = 'MONITORING.CLIENT_SEQ_UNAVAILABLE_DOT';
      return;
    }

    this.findings = [this.blankFinding()];

    this.loadStatusCodes();
    this.loadAll();
  }

  /**
   * Monitoring statuses (ref group 38) and covenant answers (ref group 46),
   * fetched together so the screen makes one pick-list call rather than two.
   */
  private loadStatusCodes(): void {
    this.pickListService
      .loadCodesAsync([PKL_GRP.MONITORING_STATUS, PKL_GRP.COVENANT_STATUS])
      .subscribe({
        next: () => {
          const options = this.pickListService.getOptionsSync(PKL_GRP.MONITORING_STATUS);

          this.covenantOptions = this.pickListService
            .getOptionsSync(PKL_GRP.COVENANT_STATUS)
            .map((option) => ({ value: Number(option.value), label: option.label }));

          // Rows may already be on screen when the codes land.
          this.resolveCovenantAnswers();

          this.statusMap = {};

          for (const option of options) {
            this.statusMap[option.value] = option.label;
          }

          if (options.length) {
            this.statusOptions = options.map((option) => ({
              value: Number(option.value),
              label: option.label,
            }));
          }

          // Rows may already be on screen, so relabel whatever is there.
          this.savedItems = this.savedItems.map((row) => this.withStatusLabel(row));
          this.pendingRows = this.pendingRows.map((row) => this.withStatusLabel(row));
          this.previousRows = this.previousRows.map((row) => this.withStatusLabel(row));
        },

        error: (error: any) => {
          console.error('Unable to load the monitoring status codes:', error);
        },
      });
  }

  /**
   * A status the pick list does not cover still has to render, so the raw key
   * is used as its own label rather than leaving the cell blank.
   */
  private withStatusLabel(row: MonitoringItemDto): MonitoringItemDto {
    const status = monitoringStatusOf(row);

    return {
      ...row,
      stsLabel: status === null ? '' : (this.statusMap[status] ?? String(status)),
    };
  }

  /** Year the findings listing is keyed on — the year of the event date. */
  get findingsYear(): number {
    const year = Number((this.evntDt || '').slice(0, 4));

    return year > 0 ? year : new Date().getFullYear();
  }

  /** Reloads the listing when the event date crosses into another year. */
  onEventDateChange(): void {
    if (this.findingsYear !== this.loadedFindingsYear) {
      this.loadSavedFindings$().subscribe();
    }
  }

  /**
   * Never errors: it reports failure through `savedItemsError` instead, so a
   * caller combining it with the other loads always completes.
   */
  private loadSavedFindings$(): Observable<void> {
    const year = this.findingsYear;

    this.savedItemsLoading = true;
    this.savedItemsError = '';
    this.loadedFindingsYear = year;

    return this.monitoringService.getNewFindings(this.clntSeq, year).pipe(
      tap((response: any) => {
        this.savedItems = this.toRows(response, 'new findings').map((row) =>
          this.withStatusLabel(row),
        );

        this.savedItemsLoading = false;
      }),

      map(() => undefined),

      catchError((error: any) => {
        console.error('Unable to load the saved monitoring findings:', error);

        this.savedItems = [];
        this.savedItemsLoading = false;
        this.savedItemsError = 'MONITORING.SAVED_ACTION_PLAN_LOAD_FAILED';

        return of(undefined);
      }),
    );
  }

  // ================= LOAD =================
  /**
   * Runs one of the two monitoring calls and reports whether it succeeded, so
   * a client that genuinely has no items is never confused with a call that
   * failed. A client with no previous year is normal, so neither call is
   * allowed to fail the screen — each degrades to its own empty grid.
   */
  private loadPanel(
    request: Observable<MonitoringItemDto[]>,
    label: string,
  ): Observable<{ ok: boolean; rows: MonitoringItemDto[] }> {
    return request.pipe(
      map((response: any) => ({ ok: true, rows: this.toRows(response, label) })),

      catchError((error: any) => {
        console.error(`Unable to load ${label} monitoring items:`, error);
        return of({ ok: false, rows: [] as MonitoringItemDto[] });
      }),
    );
  }

  private loadOutputs$(): Observable<void> {
    this.outputsLoading = true;
    this.pendingError = '';
    this.previousError = '';

    return forkJoin({
      pending: this.loadPanel(this.monitoringService.getPendingMonitoring(this.clntSeq), 'pending'),

      previous: this.loadPanel(
        this.monitoringService.getPreviousMonitoring(this.clntSeq),
        'previous year',
      ),
    }).pipe(
      map(({ pending, previous }) => {
        this.pendingRows = pending.rows.map((row) => this.withStatusLabel(row));
        this.previousRows = previous.rows.map((row) => this.withStatusLabel(row));


        this.pendingError = pending.ok ? '' : 'MONITORING.PENDING_LOAD_FAILED';

        this.previousError = previous.ok
          ? ''
          : 'MONITORING.PREVIOUS_LOAD_FAILED';

        this.outputsLoading = false;

        return undefined;
      }),
    );
  }

  /**
   * Re-reads every source the screen shows, as one operation.
   *
   * The status grid is built only once both loads have landed — rebuilding as
   * each one arrives would show the grid half-populated, and let a slow
   * response overwrite a rebuild that already used the fast one.
   *
   */
  private loadAll(): void {
    this.statusLoading = true;

    forkJoin([this.loadOutputs$(), this.loadSavedFindings$()]).subscribe({
      next: () => {
        this.statusLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to reload the monitoring data:', error);
        this.statusLoading = false;
      },
    });
  }

  /**
   * Both GETs are declared as an untyped `object` in the service's OpenAPI
   * spec, exactly like `get-covenant-responses`, which in practice returns a
   * bare array. The known envelopes are unwrapped by name and anything else
   * falls back to the first array of objects in the response, so a body that
   * gains a wrapper still renders instead of silently showing an empty grid.
   */
  private toRows(response: any, label = 'monitoring'): MonitoringItemDto[] {
    if (response === null || response === undefined || response === '') {
      return [];
    }

    const named = Array.isArray(response)
      ? response
      : Array.isArray(response?.monitoringRequestList)
        ? response.monitoringRequestList
        : Array.isArray(response?.data)
          ? response.data
          : null;

    const rows: any[] = named ?? this.findRowArray(response);

    if (!named && !rows.length) {
      // Not an error — but the body was not empty either, so the shape is one
      // this screen does not know how to read.
      console.warn(`Unrecognised ${label} monitoring response shape:`, response);
    }

    return rows.filter((row) => Number(row?.delFlg ?? 0) !== 1);
  }

  /** First array of objects anywhere in a one-level envelope. */
  private findRowArray(response: any): any[] {
    if (typeof response !== 'object') {
      return [];
    }

    for (const value of Object.values(response)) {
      if (Array.isArray(value) && value.every((item) => item && typeof item === 'object')) {
        return value;
      }
    }

    return [];
  }

  // ================= TABS =================
  setTab(tab: MonitoringTab): void {
    this.activeTab = tab;

    if (tab === 'report') {
      this.reportVisited = true;
    }

    if (tab === 'covenants') {
      // Always re-read: another screen may have answered a covenant since this
      // one last loaded.
      this.loadCovenants();
    }
  }

  // ================= FINDINGS =================
  /**
   * First ref-group-38 code. Zero only while the codes are still loading —
   * `saveFindings` refuses to send in that state rather than inventing one.
   */
  private get defaultStatus(): number {
    return this.statusOptions[0]?.value ?? 0;
  }

  private blankFinding(): MonitoringItemDto {
    return {
      // Null, not 0 — the backend assigns the key, and 0 is a value it would
      // otherwise have to interpret as "no record".
      mntrngSeq: null,
      dscrptn: '',
      rmrks: '',
      sts: this.defaultStatus,
      delFlg: 0,
    };
  }

  addFinding(): void {
    this.findings = [...this.findings, this.blankFinding()];
  }

  /**
   * Saved items are soft-deleted so the backend can reconcile them; rows that
   * were never persisted are simply dropped from the list.
   */
  removeFinding(finding: MonitoringItemDto): void {
    if (Number(finding.mntrngSeq ?? 0) > 0) {
      finding.delFlg = 1;
    } else {
      this.findings = this.findings.filter((item) => item !== finding);
    }

    if (!this.visibleFindings.length) {
      this.addFinding();
    }
  }

  get visibleFindings(): MonitoringItemDto[] {
    return this.findings.filter((finding) => Number(finding.delFlg) !== 1);
  }

  /**
   * Responding to a pending item is a re-save of the same `mntrngSeq` with an
   * updated status and remarks, so the row is carried into the findings tab
   * rather than edited in place in the read-only grid.
   */
  /**
   * Opens a monitoring item in the findings tab.
   *
   * Editing is a re-save of the same `mntrngSeq`, so the row is carried into
   * the findings form rather than edited in the read-only grid — one place
   * that writes monitoring items instead of two.
   */
  respondToPendingItem(row: MonitoringItemDto): void {
    // Only saved rows can collide; several unsaved rows all carry no key.
    const alreadyQueued = this.findings.some(
      (finding) => !!finding.mntrngSeq && finding.mntrngSeq === row.mntrngSeq,
    );

    if (alreadyQueued) {
      this.toast.info('MONITORING.ALREADY_IN_ACTION_PLAN');
      this.setTab('findings');
      return;
    }

    // Editing an existing item must keep its own event date — saving under
    // today's date would move the row to a different monitoring event.
    const rowDate = String(row.evntDt ?? '').slice(0, 10);

    if (rowDate) {
      this.evntDt = rowDate;
    }

    this.findings = [
      ...this.visibleFindings.filter((finding) => finding.dscrptn.trim() !== ''),
      {
        mntrngSeq: row.mntrngSeq ? Number(row.mntrngSeq) : null,
        dscrptn: row.dscrptn ?? '',
        rmrks: row.rmrks ?? '',

        // Keep the row's own ref code — the status is picked in the form now,
        // so forcing a constant here would silently overwrite it on save.
        sts: monitoringStatusOf(row) ?? this.defaultStatus,
        delFlg: 0,
      },
    ];

    this.setTab('findings');
  }

  saveFindings(): void {
    if (this.savingFindings) {
      return;
    }

    if (!this.evntDt) {
      this.toast.error('MONITORING.SELECT_MONITORING_VISIT_DATE');
      return;
    }

    if (!this.statusOptions.length) {
      this.toast.error('MONITORING.STATUSES_NOT_AVAILABLE');
      return;
    }

    // Deleted rows still go up so the backend can soft-delete them; blank new
    // rows are dropped rather than saved as empty findings.
    const items = this.findings.filter(
      (finding) => Number(finding.delFlg) === 1 || finding.dscrptn.trim() !== '',
    );

    if (!items.some((finding) => Number(finding.delFlg) !== 1)) {
      this.toast.error('MONITORING.ENTER_ONE_ACTION_PLAN');
      return;
    }

    const payload: SaveMonitoringPayload = {
      clntSeq: this.clntSeq,
      evntDt: this.evntDt,
      monitoringRequestList: items.map((finding) => ({
        // An edited row keeps its key so the backend updates it; a new one
        // sends null so it is inserted.
        mntrngSeq: finding.mntrngSeq ? Number(finding.mntrngSeq) : null,
        dscrptn: finding.dscrptn.trim(),
        rmrks: (finding.rmrks ?? '').trim(),
        sts: Number(finding.sts ?? this.defaultStatus),
        delFlg: Number(finding.delFlg ?? 0),
      })),
    };

    this.savingFindings = true;

    this.monitoringService.saveMonitoring(payload).subscribe({
      next: (response: any) => {
        this.savingFindings = false;

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(response?.message || 'MONITORING.ACTION_PLAN_SAVED');

        this.findings = [this.blankFinding()];

        // Reload so the saved items appear under the pending grid and in the
        // listing below the form.
        this.loadAll();
      },

      error: (error: any) => {
        console.error('Unable to save monitoring findings:', error);

        this.savingFindings = false;

        this.toast.error(error?.error?.message || error?.error?.error || 'MONITORING.ACTION_PLAN_SAVE_FAILED');
      },
    });
  }

  // ================= COVENANTS =================
  /**
   * Covenant responses for this client, limited to the covenants selected on
   * their credit proposal and joined to their question text.
   *
   * - get-covenant-questions/{crdtPrpslSeq} (once per proposal in the
   *   responses) gives the selected `qstnSeq`s; a proposal with no saved
   *   selection keeps all of its rows rather than emptying the grid.
   * - get-question-list gives the wording: `qstnSeq` → `qstSeq` → `qstStr`.
   *   A question it no longer has still renders, by sequence.
   */
  private loadCovenants(): void {
    if (this.clntSeq <= 0) {
      return;
    }

    this.covenantsLoading = true;
    this.covenantsError = '';

    forkJoin({
      responses: this.creditService.getCovenantResponsesByClient(this.clntSeq).pipe(
        catchError((error: any) => {
          console.error('Unable to load covenant responses:', error);
          return of(null);
        }),
      ),

      questions: this.setup.getQuestionList(QSTNR_SEQ.COVENANT).pipe(
        catchError((error: any) => {
          console.error('Unable to load covenant questions:', error);
          return of([]);
        }),
      ),
    })
      .pipe(
        switchMap(({ responses, questions }) => {
          const rows =
            responses === null
              ? null
              : (this.toRows(responses, 'covenant responses') as unknown as CovenantResponseDto[]);

          const proposalSeqs = [
            ...new Set((rows ?? []).map((row) => Number(row.crdtPrpslSeq ?? 0)).filter((seq) => seq > 0)),
          ];

          const selections$ = proposalSeqs.length
            ? forkJoin(
                proposalSeqs.map((seq) =>
                  this.creditService.getCovenantQuestions(seq).pipe(
                    catchError((error: any) => {
                      console.error(`Unable to load covenant questions for proposal ${seq}:`, error);
                      return of([]);
                    }),
                  ),
                ),
              )
            : of([] as any[][]);

          return selections$.pipe(
            map((lists) => ({
              rows,
              questions,
              currentQuarter: String(
                (responses as any)?.currentQrtr ?? (responses as any)?.qrtr ?? '',
              ).trim(),
              // proposal seq → its selected question seqs
              selections: new Map<number, Set<number>>(
                proposalSeqs.map((seq, index) => [
                  seq,
                  new Set(
                    (Array.isArray(lists[index]) ? lists[index] : [])
                      .filter((selected: any) => Number(selected?.delFlg ?? 0) !== 1)
                      .map((selected: any) => Number(selected?.qstnSeq)),
                  ),
                ]),
              ),
            })),
          );
        }),
      )
      .subscribe(({ rows, questions, selections, currentQuarter }) => {
        this.covenantsLoading = false;
        this.covenantQuarter = currentQuarter;

        if (rows === null) {
          this.covenantRows = [];
          this.covenantsError = 'MONITORING.COVENANTS_LOAD_FAILED';
          return;
        }

        const questionText = new Map<number, string>();

        for (const question of Array.isArray(questions) ? questions : []) {
          questionText.set(Number(question?.qstSeq), String(question?.qstStr ?? '').trim());
        }

        const visibleRows = rows.filter((row) => {
          const selected = selections.get(Number(row.crdtPrpslSeq ?? 0));
          return !selected?.size || selected.has(Number(row.qsntnSeq));
        });

        // An edit in progress survives a reload of the same row.
        const edits = new Map(this.covenantRows.map((row) => [row.crdtCnvntRspSeq, row.ansVal]));
        const remarkEdits = new Map(
          this.covenantRows
            .filter((row) => row.rmrks !== row.originalRmrks)
            .map((row) => [row.crdtCnvntRspSeq, row.rmrks]),
        );

        this.covenantRows = visibleRows.map((row) => {
          const seq = Number(row.crdtCnvntRspSeq ?? 0);
          // The monitoring answer is saved as stsKey (see saveCovenants).
          const answer = this.resolveAnswer(row.stsKey);
          const remarks = row.rmrks ?? '';

          return {
            crdtCnvntRspSeq: seq,
            crdtPrpslSeq: Number(row.crdtPrpslSeq ?? 0),
            qsntnSeq: Number(row.qsntnSeq ?? 0),
            question: this.covenantQuestionText(row, questionText),
            rmrks: remarkEdits.get(seq) ?? remarks,
            originalRmrks: remarks,
            qrtr: String(row.qrtr ?? '').trim(),
            delFlg: Number(row.delFlg ?? 0),
            ansVal: edits.get(seq) ?? answer,
            originalAnsVal: answer,
          };
        });

        this.resolveCovenantAnswers();
      });
  }

  /**
   * Wording for one covenant row.
   *
   * The by-client endpoint may already join the question in, so any text it
   * carries wins; otherwise the questionnaire is used. When neither has it the
   * row still renders by sequence — and says so once in the console, with the
   * keys it did receive, so a missing join is visible rather than silently
   * showing "Covenant #1" forever.
   */
  private covenantQuestionText(row: any, fromQuestionnaire: Map<number, string>): string {
    const inline = COVENANT_TEXT_KEYS.map((key) => String(row?.[key] ?? '').trim()).find(
      (value) => !!value,
    );

    if (inline) {
      return inline;
    }

    const joined = fromQuestionnaire.get(Number(row?.qsntnSeq));

    if (joined) {
      return joined;
    }

    if (!this.covenantTextWarned) {
      this.covenantTextWarned = true;

      console.warn(
        `No covenant wording for qsntnSeq ${row?.qsntnSeq}. Response keys:`,
        Object.keys(row ?? {}),
        '— questionnaire supplied qstSeq:',
        [...fromQuestionnaire.keys()],
      );
    }

    return this.translate.instant('MONITORING.COVENANT_FALLBACK', { seq: row?.qsntnSeq });
  }

  /**
   * `ansVal` holds a ref group 46 code as text. Anything else — a legacy
   * "1"/"0" from before that group existed, or a retired code — reads as
   * unanswered, so the dropdown only ever offers real codes.
   *
   * Reading changes nothing: an unrecognised value stays in the database
   * untouched until someone actually picks an answer for that row.
   */
  private resolveAnswer(value: string | number | null | undefined): number | null {
    const raw = String(value ?? '').trim();

    if (!raw) {
      return null;
    }

    const code = Number(raw);

    if (!Number.isFinite(code)) {
      return null;
    }

    return this.covenantOptions.some((option) => option.value === code) ? code : null;
  }

  /**
   * Re-resolves every row against the answer options.
   *
   * The rows and the pick list are fetched independently, so whichever lands
   * second reconciles the two — otherwise rows that arrived before the codes
   * would all read as unanswered.
   */
  private resolveCovenantAnswers(): void {
    this.covenantRows = this.covenantRows.map((row) => {
      const untouched = row.ansVal === row.originalAnsVal;
      const original = this.resolveAnswer(row.originalAnsVal);

      return {
        ...row,
        originalAnsVal: original,
        ansVal: untouched ? original : this.resolveAnswer(row.ansVal),
      };
    });
  }

  /** Quarter column shows the month only: "Oct-26" → "Oct". */
  quarterMonth(quarter: string): string {
    return String(quarter ?? '').split('-')[0].trim();
  }

  /** Rows whose answer or remarks the user actually changed. */
  get changedCovenantRows(): CovenantRow[] {
    return this.covenantRows.filter((row) => this.isCovenantRowChanged(row));
  }

  isCovenantRowChanged(row: CovenantRow): boolean {
    return row.ansVal !== row.originalAnsVal || row.rmrks !== row.originalRmrks;
  }

  answerLabel(value: number | null): string {
    if (value === null) {
      return 'MONITORING.NOT_ANSWERED';
    }

    return this.covenantOptions.find((option) => option.value === value)?.label ?? String(value);
  }

  resetCovenants(): void {
    this.covenantRows = this.covenantRows.map((row) => ({
      ...row,
      ansVal: row.originalAnsVal,
      rmrks: row.originalRmrks,
    }));
  }

  /**
   * Saves the changed answers.
   *
   * The payload carries one `crdtPrpslSeq` for the whole batch, but a client's
   * covenants can span proposals, so rows are grouped by proposal and sent as
   * one request each — a single call would file them all under one proposal.
   *
   * `rmrks` is sent with every row (edited or not): the endpoint saves a
   * whole response, so omitting it would blank the remarks.
   *
   * The answer goes up as `stsKey` (no `ansVal`). One cleared back to
   * "Not answered" goes up as `null` rather than 0 —
   * 0 is a valid ref code shape, so sending it would record an answer nobody
   * chose.
   */
  saveCovenants(): void {
    if (this.savingCovenants) {
      return;
    }

    const changed = this.changedCovenantRows;

    if (!changed.length) {
      this.toast.info('MONITORING.NO_COVENANT_CHANGES');
      return;
    }

    const byProposal = new Map<number, CovenantRow[]>();

    for (const row of changed) {
      byProposal.set(row.crdtPrpslSeq, [...(byProposal.get(row.crdtPrpslSeq) ?? []), row]);
    }

    const requests = [...byProposal.entries()].map(([crdtPrpslSeq, rows]) => {
      const payload: SaveCovenantResponsesPayload = {
        crdtPrpslSeq,
        responses: rows.map((row) => ({
          crdtCnvntRspSeq: row.crdtCnvntRspSeq,
          qsntnSeq: row.qsntnSeq,
          // Monitoring records its answer in stsKey; ansVal is not sent.
          stsKey: row.ansVal === null ? null : Number(row.ansVal),
          rmrks: row.rmrks,
          qrtr: row.qrtr,
          delFlg: row.delFlg,
        })),
      };

      return this.creditService.saveCovenantResponses(payload);
    });

    this.savingCovenants = true;

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        this.savingCovenants = false;

        const failed = responses.find((response) => response?.error);

        if (failed) {
          this.toast.error(failed.error);
          return;
        }

        this.toast.success(
          changed.length === 1
            ? 'MONITORING.COVENANT_RESPONSE_SAVED'
            : this.translate.instant('MONITORING.RESPONSES_SAVED', { count: changed.length }),
        );

        // Re-read rather than trusting the edit — this is the only
        // confirmation that what was sent is what was stored.
        this.covenantRows = [];
        this.loadCovenants();
      },

      error: (error: any) => {
        console.error('Unable to save the covenant responses:', error);

        this.savingCovenants = false;

        this.toast.error(
          error?.error?.message || error?.error?.error || 'MONITORING.RESPONSES_SAVE_FAILED',
        );
      },
    });
  }

}
